import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 12,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 20,
    borderBottom: '2 solid #333',
    paddingBottom: 10,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#1f2937',
  },
  subtitle: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 10,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#1f2937',
    borderBottom: '1 solid #e5e7eb',
    paddingBottom: 5,
  },
  grid: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 10,
  },
  gridItem: {
    flex: 1,
    marginRight: 20,
  },
  label: {
    fontSize: 10,
    color: '#6b7280',
    marginBottom: 2,
  },
  value: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1f2937',
  },
  summary: {
    backgroundColor: '#f0f9ff',
    padding: 15,
    borderRadius: 5,
    marginTop: 20,
  },
  summaryTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1e40af',
    marginBottom: 10,
  },
  summaryGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  summaryItem: {
    width: '50%',
    marginBottom: 10,
  },
  table: {
    marginTop: 10,
    border: '1 solid #e5e7eb',
  },
  tableHeader: {
    backgroundColor: '#f9fafb',
    flexDirection: 'row',
    borderBottom: '1 solid #e5e7eb',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #e5e7eb',
  },
  tableCell: {
    flex: 1,
    padding: 8,
    fontSize: 10,
  },
  tableCellHeader: {
    fontWeight: 'bold',
    backgroundColor: '#f9fafb',
  },
});

interface PDFDocumentProps {
  projectName: string;
  valuationType: string;
  investmentAmount: number;
  equityPercentage: number;
  soloPrice: number;
  teamPrice: number;
  unlimitedPrice: number;
  monthlyOpex: number;
  initialUsers: number;
  monthlyGrowthRate: number;
  revenueMultiple: number;
  soloGrowthRate: number;
  teamGrowthRate: number;
  unlimitedGrowthRate: number;
  projectionData: any[];
  breakEvenMonth: number | null;
  finalMetrics: any;
  companyValuation: number;
  investorEquityValue: number;
  roi: number;
  formatCurrency: (value: number) => string;
  chartImages?: {
    cashChart?: string;
    revenueChart?: string;
    expensesChart?: string;
    usersChart?: string;
  };
}

export const InvestmentPlanPDF: React.FC<PDFDocumentProps> = ({
  projectName,
  valuationType,
  investmentAmount,
  equityPercentage,
  soloPrice,
  teamPrice,
  unlimitedPrice,
  monthlyOpex,
  initialUsers,
  monthlyGrowthRate,
  revenueMultiple,
  soloGrowthRate,
  teamGrowthRate,
  unlimitedGrowthRate,
  projectionData,
  breakEvenMonth,
  finalMetrics,
  companyValuation,
  investorEquityValue,
  roi,
  formatCurrency,
  chartImages,
}) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <Text style={styles.title}>{projectName} - SaaS Investment Plan</Text>
        <Text style={styles.subtitle}>Professional Investment Analysis & Financial Projections</Text>
        <Text>Generated on: {new Date().toLocaleDateString()}</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Investment Overview</Text>
        <View style={styles.grid}>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Funding Source</Text>
            <Text style={styles.value}>{valuationType.toUpperCase()}</Text>
          </View>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Investment Amount</Text>
            <Text style={styles.value}>{formatCurrency(investmentAmount)}</Text>
          </View>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Equity Stake</Text>
            <Text style={styles.value}>{equityPercentage}%</Text>
          </View>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Revenue Multiple</Text>
            <Text style={styles.value}>{revenueMultiple}x</Text>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Pricing Strategy</Text>
        <View style={styles.grid}>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Solo Plan (Monthly)</Text>
            <Text style={styles.value}>{formatCurrency(soloPrice)}</Text>
          </View>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Team Plan (5 Users)</Text>
            <Text style={styles.value}>{formatCurrency(teamPrice)}</Text>
          </View>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Unlimited Plan</Text>
            <Text style={styles.value}>{formatCurrency(unlimitedPrice)}</Text>
          </View>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Monthly OPEX</Text>
            <Text style={styles.value}>{formatCurrency(monthlyOpex)}</Text>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Growth Assumptions</Text>
        <View style={styles.grid}>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Initial Users</Text>
            <Text style={styles.value}>{initialUsers.toLocaleString()}</Text>
          </View>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Overall Growth Rate</Text>
            <Text style={styles.value}>{monthlyGrowthRate}%/month</Text>
          </View>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Solo Plan Growth</Text>
            <Text style={styles.value}>{soloGrowthRate}%/month</Text>
          </View>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Team Plan Growth</Text>
            <Text style={styles.value}>{teamGrowthRate}%/month</Text>
          </View>
        </View>
        <View style={[styles.grid, { marginTop: 10 }]}>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Unlimited Plan Growth</Text>
            <Text style={styles.value}>{unlimitedGrowthRate}%/month</Text>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Key Financial Metrics</Text>
        <View style={styles.grid}>
          <View style={styles.gridItem}>
            <Text style={styles.label}>3-Year ROI</Text>
            <Text style={styles.value}>{roi.toFixed(1)}%</Text>
          </View>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Break-Even Month</Text>
            <Text style={styles.value}>{breakEvenMonth ? `Month ${breakEvenMonth}` : 'Not reached'}</Text>
          </View>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Year 3 Users</Text>
            <Text style={styles.value}>{finalMetrics.users.toLocaleString()}</Text>
          </View>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Investor Equity Value</Text>
            <Text style={styles.value}>{formatCurrency(investorEquityValue)}</Text>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Key Projections</Text>
        <View style={styles.grid}>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Month 12 Revenue</Text>
            <Text style={styles.value}>{formatCurrency(projectionData[11]?.revenue || 0)}</Text>
          </View>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Month 12 Users</Text>
            <Text style={styles.value}>{projectionData[11]?.users?.toLocaleString() || 0}</Text>
          </View>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Month 12 Cash Position</Text>
            <Text style={styles.value}>{formatCurrency(projectionData[11]?.cumulativeCash || 0)}</Text>
          </View>
          <View style={styles.gridItem}>
            <Text style={styles.label}>Month 24 Revenue</Text>
            <Text style={styles.value}>{formatCurrency(projectionData[23]?.revenue || 0)}</Text>
          </View>
        </View>
      </View>

      {chartImages && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Financial Charts</Text>
          {chartImages.cashChart && (
            <View style={styles.section}>
              <Text style={styles.subtitle}>Cumulative Cash Position</Text>
              <Image src={chartImages.cashChart} style={{ width: '100%', height: 200 }} />
            </View>
          )}
          {chartImages.revenueChart && (
            <View style={styles.section}>
              <Text style={styles.subtitle}>Revenue by Pricing Tier</Text>
              <Image src={chartImages.revenueChart} style={{ width: '100%', height: 200 }} />
            </View>
          )}
          {chartImages.expensesChart && (
            <View style={styles.section}>
              <Text style={styles.subtitle}>Monthly Revenue vs Expenses</Text>
              <Image src={chartImages.expensesChart} style={{ width: '100%', height: 200 }} />
            </View>
          )}
          {chartImages.usersChart && (
            <View style={styles.section}>
              <Text style={styles.subtitle}>User Growth</Text>
              <Image src={chartImages.usersChart} style={{ width: '100%', height: 200 }} />
            </View>
          )}
        </View>
      )}

      <View style={styles.summary}>
        <Text style={styles.summaryTitle}>3-Year Financial Summary</Text>
        <View style={styles.summaryGrid}>
          <View style={styles.summaryItem}>
            <Text style={styles.label}>Total Revenue</Text>
            <Text style={styles.value}>{formatCurrency(finalMetrics.totalRevenue)}</Text>
          </View>
          <View style={styles.summaryItem}>
            <Text style={styles.label}>Total Expenses</Text>
            <Text style={styles.value}>{formatCurrency(monthlyOpex * 36)}</Text>
          </View>
          <View style={styles.summaryItem}>
            <Text style={styles.label}>Net Profit</Text>
            <Text style={styles.value}>{formatCurrency(finalMetrics.totalRevenue - monthlyOpex * 36)}</Text>
          </View>
          <View style={styles.summaryItem}>
            <Text style={styles.label}>Company Valuation</Text>
            <Text style={styles.value}>{formatCurrency(companyValuation)}</Text>
          </View>
          <View style={styles.summaryItem}>
            <Text style={styles.label}>Investor Returns</Text>
            <Text style={styles.value}>{formatCurrency(investorEquityValue)}</Text>
          </View>
          <View style={styles.summaryItem}>
            <Text style={styles.label}>ROI Multiple</Text>
            <Text style={styles.value}>{((investorEquityValue / investmentAmount) - 1).toFixed(2)}x</Text>
          </View>
        </View>
      </View>

      <View style={[styles.section, { marginTop: 20 }]}>
        <Text style={{ fontSize: 10, color: '#6b7280', textAlign: 'center' }}>
          This investment plan is for illustrative purposes only and does not constitute financial advice.
          Past performance does not guarantee future results.
        </Text>
      </View>
    </Page>
  </Document>
);